import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, FileText, Wand2, Upload } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Divisor {
  id: number;
  title: string;
  divisor_type: string;
  content_preview: string;
  parent_id: number | null;
  has_children: boolean;
}

interface DivisorsJsonMapCreateProps {
  document: {
    id: number;
    name: string;
  };
  extractJob: {
    id: number;
    job_id: string;
    name: string;
  };
  divisors: Divisor[];
  divisorTypes: string[];
}

export default function DivisorsJsonMapCreate({
  document,
  extractJob,
  divisors,
  divisorTypes
}: DivisorsJsonMapCreateProps) {
  const [selectedDivisorIds, setSelectedDivisorIds] = useState<Set<number>>(new Set());
  const [selectedSampleTypes, setSelectedSampleTypes] = useState<string[]>([]); // Changed to array to preserve order
  const [jsonInput, setJsonInput] = useState('');
  const [useGeneratedSample, setUseGeneratedSample] = useState(false);

  // Valid divisor types from the backend
  const validDivisorTypes = [
    'title', 'volume', 'part', 'division', 'subdivision', 
    'article', 'chapter', 'section', 'subsection', 'clause', 
    'subclause', 'paragraph', 'subchapter', 'provision', 'subpart', 'URL'
  ];

  // Group divisors by type for better organization
  const divisorsByType = divisors.reduce((acc, divisor) => {
    if (!acc[divisor.divisor_type]) {
      acc[divisor.divisor_type] = [];
    }
    acc[divisor.divisor_type].push(divisor);
    return acc;
  }, {} as Record<string, Divisor[]>);

  // Generate sample JSON from selected divisors or types
  const generateSampleJson = () => {
    let sampleStructure: any[] = [];

    if (divisors.length > 0 && selectedDivisorIds.size > 0) {
      // Use existing divisors if selected
      const selectedDivisors = divisors.filter(d => selectedDivisorIds.has(d.id));
      
      // Build hierarchical structure based on selected divisors
      const buildHierarchy = (parentId: number | null = null): any[] => {
        return selectedDivisors
          .filter(d => d.parent_id === parentId)
          .map(divisor => {
            const children = buildHierarchy(divisor.id);
            return {
              title: divisor.title,
              divisor_type: divisor.divisor_type,
              content_preview: divisor.content_preview,
              ...(children.length > 0 && { children })
            };
          });
      };

      sampleStructure = buildHierarchy();
    } else if (selectedSampleTypes.length > 0) {
      // Generate hierarchical sample structure from selected types in order
      const typesInOrder = selectedSampleTypes; // Already in selection order
      
      // Build nested structure based on selection order
      const buildNestedStructure = (types: string[], level: number = 0): any => {
        if (level >= types.length) return null;
        
        const currentType = types[level];
        const result: any = {
          title: `Sample ${currentType.charAt(0).toUpperCase() + currentType.slice(1)} Title`,
          divisor_type: currentType,
          content_preview: `This is sample content for a ${currentType}`,
        };

        const childStructure = buildNestedStructure(types, level + 1);
        if (childStructure) {
          result.children = [childStructure];
        }

        return result;
      };

      const nested = buildNestedStructure(typesInOrder);
      sampleStructure = nested ? [nested] : [];
    }

    if (sampleStructure.length === 0) {
      alert('Please select at least one divisor or divisor type to generate sample JSON');
      return;
    }

    const sampleJson = JSON.stringify(sampleStructure, null, 2);
    setJsonInput(sampleJson);
    setUseGeneratedSample(true);
  };

  // Handle divisor selection
  const handleDivisorSelection = (divisorId: number, checked: boolean) => {
    const newSelection = new Set(selectedDivisorIds);
    if (checked) {
      newSelection.add(divisorId);
    } else {
      newSelection.delete(divisorId);
    }
    setSelectedDivisorIds(newSelection);
  };

  // Handle selecting all divisors of a type
  const handleSelectAllType = (divisorType: string, checked: boolean) => {
    const newSelection = new Set(selectedDivisorIds);
    const typeDivisors = divisorsByType[divisorType] || [];
    
    typeDivisors.forEach(divisor => {
      if (checked) {
        newSelection.add(divisor.id);
      } else {
        newSelection.delete(divisor.id);
      }
    });
    
    setSelectedDivisorIds(newSelection);
  };

  // Handle selecting sample divisor types (order matters!)
  const handleSampleTypeSelection = (type: string, checked: boolean) => {
    if (checked) {
      // Add to end of array if not already present
      if (!selectedSampleTypes.includes(type)) {
        setSelectedSampleTypes([...selectedSampleTypes, type]);
      }
    } else {
      // Remove from array
      setSelectedSampleTypes(selectedSampleTypes.filter(t => t !== type));
    }
  };

  // Submit the JSON map
  const handleSubmit = () => {
    if (!jsonInput.trim()) {
      alert('Please provide JSON data or generate a sample');
      return;
    }

    try {
      // Validate JSON
      JSON.parse(jsonInput);
      
      // Submit to the create_from_json_map endpoint
      router.post(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/create_from_json_map`, {
        json_data: jsonInput
      });
    } catch (e) {
      alert('Invalid JSON format. Please check your input.');
    }
  };

  const breadcrumbItems = [
    { label: 'Documents', href: '/documents' },
    { label: document.name, href: `/documents/${document.id}` },
    { label: 'Extract Jobs', href: `/documents/${document.id}/extract_jobs` },
    { label: extractJob.name, href: `/documents/${document.id}/extract_jobs/${extractJob.id}` },
    { label: 'Divisors', href: `/documents/${document.id}/extract_jobs/${extractJob.id}/divisors` },
    { label: 'Create from JSON Map' }
  ];

  return (
    <>
      <Head title="Create Divisors from JSON Map" />
      
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Breadcrumbs opsConsole items={breadcrumbItems} />
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button asChild variant="ghost" size="sm">
              <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Divisors
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Create Divisors from JSON Map</h1>
              <p className="text-gray-600">
                Select divisors to generate sample JSON, or paste your own JSON structure
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Divisor Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Select Divisors for Sample JSON
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {divisors.length > 0 ? (
                // Show existing divisors
                <>
                  <div className="text-sm text-gray-600 mb-4">
                    Select from existing divisors in this extract job:
                  </div>
                  {divisorTypes.map(type => (
                    <div key={type} className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={(divisorsByType[type] || []).every(d => selectedDivisorIds.has(d.id))}
                          onCheckedChange={(checked) => handleSelectAllType(type, checked as boolean)}
                        />
                        <Label className="font-semibold text-sm uppercase tracking-wide">
                          {type}
                        </Label>
                      </div>
                      
                      <div className="ml-6 space-y-1">
                        {(divisorsByType[type] || []).map(divisor => (
                          <div key={divisor.id} className="flex items-center space-x-2">
                            <Checkbox
                              checked={selectedDivisorIds.has(divisor.id)}
                              onCheckedChange={(checked) => handleDivisorSelection(divisor.id, checked as boolean)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{divisor.title}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {divisor.content_preview}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                // Show sample divisor types when no existing divisors
                <>
                  <div className="text-sm text-gray-600 mb-4">
                    No existing divisors found. Select divisor types in hierarchical order (each selected type will nest under the previous):
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500 mb-3">
                      Order matters! Selected types will be nested: title → volume → part → division → etc.
                    </div>
                    {validDivisorTypes.map((type, index) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          checked={selectedSampleTypes.includes(type)}
                          onCheckedChange={(checked) => handleSampleTypeSelection(type, checked as boolean)}
                        />
                        <Label className="text-sm capitalize flex items-center">
                          <span className="w-6 text-xs text-gray-400 mr-2">{index + 1}.</span>
                          {type}
                          {selectedSampleTypes.includes(type) && (
                            <span className="ml-2 text-xs text-blue-600">
                              (Level {selectedSampleTypes.indexOf(type) + 1})
                            </span>
                          )}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  {selectedSampleTypes.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded text-sm">
                      <div className="font-medium text-blue-800 mb-1">Hierarchy Preview (in selection order):</div>
                      <div className="text-blue-700 whitespace-pre-line">
                        {selectedSampleTypes
                          .map((type, index) => (
                            `${'  '.repeat(index)}${index + 1}. ${type}${index < selectedSampleTypes.length - 1 ? ' →\n' : ''}`
                          ))
                          .join('')
                        }
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <div className="pt-4 border-t">
                <Button 
                  onClick={generateSampleJson}
                  className="w-full"
                  variant="outline"
                  disabled={
                    divisors.length > 0 
                      ? selectedDivisorIds.size === 0 
                      : selectedSampleTypes.length === 0
                  }
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Sample JSON (
                    {divisors.length > 0 
                      ? `${selectedDivisorIds.size} divisors` 
                      : `${selectedSampleTypes.length} types`
                    } selected)
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - JSON Input */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Upload className="h-5 w-5 mr-2" />
                JSON Map Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="json-input">
                  Paste your JSON map or use generated sample:
                </Label>
                <Textarea
                  id="json-input"
                  placeholder="Paste your JSON map here or generate a sample from selected divisors..."
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                />
              </div>
              
              {useGeneratedSample && (
                <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded">
                  Sample JSON generated from selected divisors. You can edit this or replace it with your own JSON.
                </div>
              )}
              
              <div className="flex space-x-2">
                <Button 
                  onClick={handleSubmit}
                  className="flex-1"
                  disabled={!jsonInput.trim()}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create Divisors
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}